import { createApi } from '@reduxjs/toolkit/query/react';
import {
  apiResource,
  b2chatBaseQuery,
  B2ChatClientContacts,
  B2ChatClientMercadoPago,
} from '@src/client';
import B2ChatClient from '@src/client';
import {
  ContactInfo,
  PaginatedPayments,
} from '@src/components/AdminHome/Integrations/MercadoPago/types';

const mercadoPagoApi = createApi({
  reducerPath: 'mercadoPagoApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['mercadoPagoStatus', 'payments'],
  endpoints: ({ query, mutation }) => ({
    checkStatus: query<boolean, void>({
      providesTags: ['mercadoPagoStatus'],
      extraOptions: { maxRetries: 0 },
      query: () =>
        apiResource(
          B2ChatClientMercadoPago.resources.mercadoPago.actions.checkStatus,
          {
            withJWT: true,
            transformError: () => ({ data: false }),
          }
        ),
      transformResponse: (data: unknown) => data !== false,
    }),

    getJwtInstallationApp: query<{ token: string }, string>({
      extraOptions: { maxRetries: 0 },
      query: appId =>
        apiResource(B2ChatClient.resources.apps.actions.getJwtInstallationApp, {
          params: { appId },
        }),
    }),

    disconnect: mutation<void, void>({
      invalidatesTags: ['mercadoPagoStatus'],
      query: () =>
        apiResource(
          B2ChatClientMercadoPago.resources.mercadoPago.actions.disconnect,
          { withJWT: true }
        ),
    }),

    getPayments: query<PaginatedPayments, { page: number; size: number }>({
      providesTags: (_result, _error, { page, size }) => [
        { type: 'payments', id: `${page}-${size}` },
      ],
      extraOptions: { maxRetries: 0 },
      query: ({ page = 0, size = 20 }) =>
        apiResource(
          B2ChatClientMercadoPago.resources.mercadoPago.actions.getPayments,
          { withJWT: true, params: { page, size } }
        ),
      transformResponse: (data: unknown): PaginatedPayments => {
        const d = data as Record<string, unknown>;
        return {
          content: (d.content ??
            d.data ??
            d.payments ??
            []) as PaginatedPayments['content'],
          totalPages: (d.totalPages as number) ?? 1,
          totalElements: (d.totalElements as number) ?? 0,
          number: (d.number as number) ?? 0,
        };
      },
    }),

    getContactsByIds: query<ContactInfo[], number[]>({
      extraOptions: { maxRetries: 0 },
      query: ids =>
        apiResource(
          B2ChatClientContacts.resources.contacts.actions.getContactsByIds,
          { data: { ids }, withJWT: true }
        ),
      transformResponse: (data: unknown) => {
        if (Array.isArray(data)) return data as ContactInfo[];
        const d = data as Record<string, unknown>;
        return (d.contacts ?? d.content ?? []) as ContactInfo[];
      },
    }),
  }),
});

export const {
  useCheckStatusQuery,
  useDisconnectMutation,
  useGetPaymentsQuery,
  useLazyGetPaymentsQuery,
  useGetContactsByIdsQuery,
  useLazyGetJwtInstallationAppQuery,
} = mercadoPagoApi;

export default mercadoPagoApi;
